import { type ElementType } from 'react';
import { type MessageDescriptor } from 'dibs-react-intl';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { pageTypeConstants as pageTypes } from '../../../constants/pageTypeConstants.ts';
import {
    FILTER_SEARCH,
    FILTER_REFINE_SEARCH,
} from '../../../finding/SbSharedRefineMenu/sbSharedRefineMenuConstants.js';

import {
    getCategory,
    getCategories,
    categorySortOrder,
    getLowestCategory,
    getCategoryCode,
    getCategoryUrlLabel,
    getPageLevel,
    isCategory,
    CATEGORY_LEVEL_1,
    getCapitalizedFirstLetter,
    type Filters,
} from '../../../utils/categoryHelpers.ts';
import { SortMap } from '../../../constants/sbConstants.ts';

export type RefineMenuFiltersMap = Record<
    string,
    { component?: ElementType | null; title?: MessageDescriptor; isBatchAppliedFilter?: boolean }
>;

const paramsConfig = {
    seller: 'dealer',
    location: 'loc',
    period: 'per',
    item_type: 'item-type',
    lead_time_bucket: 'production-time-frame',
    matchingSets: 'matching-sets',
};

const getAppliedCategoriesDisplayNames = (
    categories: Filters,
    topCategoryL1: string | null
): string[] => {
    return categorySortOrder
        .map(categoryName => {
            const category =
                categoryName === CATEGORY_LEVEL_1
                    ? topCategoryL1 || ''
                    : getCategoryUrlLabel(getCategory(categories, categoryName));
            return getCapitalizedFirstLetter(category);
        })
        .filter(Boolean);
};

const getPageSortLabel = (
    sortValue: string | null | undefined
): (typeof SortMap)[keyof typeof SortMap] | null => {
    if (sortValue && hasKey(SortMap, sortValue)) {
        return SortMap[sortValue];
    }
    return null;
};

const getFilterUrlLabel = (appliedFilters: Filters, filterName: string): string => {
    const currentFilter =
        (appliedFilters || []).filter(filter => filter?.name === filterName)?.[0] || null;
    return getCategoryUrlLabel(currentFilter);
};

const getPageSubType = (pageType: string | null, searchCorrectionTerm: string | null): string => {
    const map = {
        origin: 'Place of Origin',
        dealer: 'Dealers',
    } as const;
    let pageSubType = '';

    if (searchCorrectionTerm && pageType !== pageTypes.SEARCH) {
        pageSubType = 'Semantic';
    } else if (pageType) {
        pageSubType = hasKey(map, pageType) ? map[pageType] : getCapitalizedFirstLetter(pageType);
    }
    return pageSubType;
};

const getPageviewData = ({
    pageType,
    searchCorrectionTerm,
    appliedFilters = [],
    totalResults,
    pageNumber,
    optPageURL,
    sortValue,
    isModule,
    refineMenuFiltersMap,
    topCategoryL1,
    searchType,
}: {
    pageType: string | null;
    searchCorrectionTerm: string | null;
    appliedFilters: Filters;
    totalResults: number;
    pageNumber: number;
    optPageURL: string | null;
    sortValue: string | null | undefined;
    isModule: boolean;
    refineMenuFiltersMap?: RefineMenuFiltersMap;
    topCategoryL1: string | null;
    searchType: string | null;
}):
    | {
          pageType: 'Results';
          opt_pageURL?: string;
          countOfItems: number;
          pageSubType: string;
          pageSection: string;
      }
    | {
          pageType: 'Results';
          pageSubType: string;
          pageSection: string;
          categoryCode?: string;
          pageCategory: string;
          pageLevel: string;
          countOfItems: number;
          contentModule: string;
          pageNumberViewed: number;
          pageSort: string | null;
          opt_pageURL?: string;
          viewMode: 'grid';
          searchData: {
              applied_filters: Record<string, string[]>;
              categories: string[];
              search_term: string;
              section: string;
          };
          search_type: string | null;
      } => {
    const categories = getCategories(appliedFilters);
    const lowestCategory = getLowestCategory(categories);
    const appliedCategoriesDisplayNames = getAppliedCategoriesDisplayNames(
        categories,
        topCategoryL1
    );

    const pageLevel = getPageLevel(lowestCategory);
    const categoryCode = getCategoryCode(lowestCategory);

    // For L1s GA just wants the vertical label not the whole SEO name
    // i.e 'Jewelry' instead of 'Jewelry & Watches'
    // and 'Furniture' instead of '18th Century Furniture'
    const pageCategory = getCapitalizedFirstLetter(topCategoryL1);

    const pageSection = categories.map(getCategoryUrlLabel).join('/');
    const pageSort = getPageSortLabel(sortValue);
    const searchTerm =
        getFilterUrlLabel(appliedFilters, FILTER_SEARCH) ||
        getFilterUrlLabel(appliedFilters, FILTER_REFINE_SEARCH) ||
        '';
    const section = getFilterUrlLabel(appliedFilters, pageTypes.SECTION) || '';
    const pageSubType = getPageSubType(pageType, searchCorrectionTerm);
    const contentModule = isModule ? 'y' : 'n';

    if (pageType === pageTypes.BUY) {
        return {
            pageType: 'Results',
            opt_pageURL: undefined,
            countOfItems: totalResults,
            pageSubType,
            pageSection,
        };
    }

    // object of all non-category applied filters keyed by name
    // ex. { color: ['red', 'silver'] }
    const appliedFilterSearchData = (appliedFilters || [])
        .filter(
            f =>
                !isCategory(f) &&
                f?.name &&
                hasKey(refineMenuFiltersMap, f.name) &&
                f.name !== FILTER_REFINE_SEARCH
        )
        .reduce(
            (data, filter) => {
                if (filter?.name) {
                    const valueDisplayNames = (filter?.values || []).map(value =>
                        getCapitalizedFirstLetter(value?.urlLabel)
                    );
                    const filerName = hasKey(paramsConfig, filter.name)
                        ? paramsConfig[filter.name]
                        : filter.name;
                    data[filerName] = valueDisplayNames;
                }

                return data;
            },
            {} as Record<string, string[]>
        );

    let appliedOptPageURL = optPageURL ? `${optPageURL}` : undefined;
    const searchData = {
        applied_filters: appliedFilterSearchData,
        categories: appliedCategoriesDisplayNames,
        search_term: searchCorrectionTerm || searchTerm,
        section,
    };

    if (searchCorrectionTerm && pageType !== pageTypes.SEARCH) {
        const [segments, urlParams] = (optPageURL || '').split('?');
        const params = new URLSearchParams(urlParams || '');
        params.set('oq', searchCorrectionTerm);
        params.sort();
        const paramsString = decodeURIComponent(params.toString());
        appliedOptPageURL = `/semantic${segments}?${paramsString}`;
    }

    return {
        pageType: 'Results',
        pageSubType,
        categoryCode,
        pageCategory,
        pageLevel,
        pageSection,
        countOfItems: totalResults,
        contentModule,
        pageNumberViewed: pageNumber,
        pageSort,
        opt_pageURL: appliedOptPageURL,
        viewMode: 'grid',
        searchData,
        search_type: searchType,
    };
};

export { getPageviewData };
